/**
 * Hook for orchestrating the full render pipeline for a single scene.
 * Uses existing hooks for each step — does NOT duplicate generation logic.
 *
 * Pipeline: Audio + Last Frame Image (parallel) -> Video -> Mux
 */
import { useState, useCallback } from 'react';
import type { Id } from '../../convex/_generated/dataModel';
import type { IRenderSceneState, FrameMode } from '@/@types/sceneEditorV2';

interface IUseRenderSceneDeps {
  generateAudio: (sceneId: Id<'reelScenes'>, voiceId: Id<'clonedVoices'>) => Promise<unknown>;
  regenerateLastFrame: (sceneId: Id<'reelScenes'>) => Promise<unknown>;
  generateVideo: (sceneId: Id<'reelScenes'>) => Promise<unknown>;
}

interface IUseRenderSceneReturn {
  state: IRenderSceneState;
  renderScene: (sceneId: Id<'reelScenes'>, voiceId: Id<'clonedVoices'>, frameMode: FrameMode) => Promise<void>;
  reset: () => void;
  isRendering: boolean;
}

const INITIAL_STATE: IRenderSceneState = {
  step: 'idle',
  sceneId: null,
  error: null,
  audioReady: false,
  lastFrameReady: false,
};

export function useRenderScene(deps: IUseRenderSceneDeps): IUseRenderSceneReturn {
  const [state, setState] = useState<IRenderSceneState>(INITIAL_STATE);

  const renderScene = useCallback(async (
    sceneId: Id<'reelScenes'>,
    voiceId: Id<'clonedVoices'>,
    frameMode: FrameMode,
  ) => {
    setState({
      step: 'generating-audio',
      sceneId,
      error: null,
      audioReady: false,
      lastFrameReady: frameMode === 'start-only',
    });

    try {
      // Step 1: Parallel — audio + last frame image
      const promises: Promise<unknown>[] = [];

      // Always generate audio
      promises.push(
        deps.generateAudio(sceneId, voiceId).then(() => {
          setState(prev => ({ ...prev, audioReady: true }));
        })
      );

      // Generate last frame only if using 2-frame mode
      if (frameMode === 'start-end') {
        setState(prev => ({ ...prev, step: 'generating-last-frame' }));
        promises.push(
          deps.regenerateLastFrame(sceneId).then(() => {
            setState(prev => ({ ...prev, lastFrameReady: true }));
          })
        );
      }

      await Promise.all(promises);

      // Step 2: Generate video
      setState(prev => ({ ...prev, step: 'generating-video' }));
      await deps.generateVideo(sceneId);

      // Video generation is webhook-based — status updates reactively.
      // Set to completed after video gen action returns (webhook will update scene status).
      setState(prev => ({ ...prev, step: 'completed' }));

    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Render failed';
      setState(prev => ({ ...prev, step: 'failed', error: message }));
    }
  }, [deps]);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const isRendering = state.step !== 'idle' && state.step !== 'completed' && state.step !== 'failed';

  return {
    state,
    renderScene,
    reset,
    isRendering,
  };
}
